import React from 'react';
import { FiCheckCircle, FiAlertTriangle, FiAlertOctagon } from 'react-icons/fi';
import {
  useAgentHealthScore,
  useAgentScores,
} from '../../hooks/useAgentAnalytics';
import type { AnalyticsDateRange, AnalyticsPeriod } from '../../services/agentAnalyticsApi';
import { getMetricLabel } from '../../utils/metric-labels';

interface QualityScoreCardProps {
  agentId: string;
  period: AnalyticsPeriod;
  /** Intervalo personalizado — substitui a janela do `period` */
  range?: AnalyticsDateRange;
}

function scoreColor(score: number): string {
  if (score >= 0.8) return '#10b981'; // green
  if (score >= 0.5) return '#f59e0b'; // amber
  return '#ef4444'; // red
}

function scoreIcon(score: number): React.ReactNode {
  if (score >= 0.8) return <FiCheckCircle className="h-5 w-5" />;
  if (score >= 0.5) return <FiAlertTriangle className="h-5 w-5" />;
  return <FiAlertOctagon className="h-5 w-5" />;
}

export const QualityScoreCard: React.FC<QualityScoreCardProps> = ({
  agentId,
  period,
  range,
}) => {
  const { data: health, isLoading: loadingHealth } = useAgentHealthScore(
    agentId,
    period,
    range,
  );
  const { data: scores, isLoading: loadingScores } = useAgentScores(
    agentId,
    period,
    range,
  );

  const isLoading = loadingHealth || loadingScores;

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-sm font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Qualidade geral
        </h3>
        {health && health.hasEvaluators && (
          <div style={{ color: scoreColor(health.overallScore) }}>
            {scoreIcon(health.overallScore)}
          </div>
        )}
      </div>

      {isLoading && (
        <div
          className="text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Carregando...
        </div>
      )}

      {!isLoading && health && !health.hasEvaluators && (
        <div
          className="text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Nenhum evaluator configurado. Configure os avaliadores de qualidade
          para ver scores agregados aqui.
        </div>
      )}

      {!isLoading && health && health.hasEvaluators && (
        <>
          <div
            className="text-4xl font-bold mb-3"
            style={{ color: scoreColor(health.overallScore) }}
          >
            {Math.round(health.overallScore * 100)}%
          </div>

          <div className="space-y-2">
            {scores &&
              Object.entries(scores.byMetric).map(([name, metric]) => (
                <div
                  key={name}
                  className="flex items-center justify-between text-sm"
                >
                  <span style={{ color: 'var(--theme-muted-text)' }}>
                    {getMetricLabel(name)}
                  </span>
                  <span
                    className="font-medium"
                    style={{ color: scoreColor(metric.average) }}
                  >
                    {Math.round(metric.average * 100)}%
                  </span>
                </div>
              ))}
          </div>

          <div
            className="mt-3 text-xs"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Baseado em {health.traceCount} conversas avaliadas
          </div>
        </>
      )}
    </div>
  );
};
